import { toLiveMatchData, type LiveEventSnapshot } from "@/features/live-data/snapshots";
import type { MatchRow } from "@/utilities/data/competition/derive/types";
import type { LiveOverride } from "@/utilities/mappers/match";

/** Statuses that render and poll as in play. */
export const IN_PLAY_STATUSES: ReadonlySet<string> = new Set(["LIVE", "INTERRUPTED"]);

/** The feed's current minute as display text, or null when absent or not yet positive. */
function storedMinute(match: MatchRow): string | null {
    const minute = match.timing?.currentMinute;
    return typeof minute === "number" && Number.isFinite(minute) && minute > 0 ? String(minute) : null;
}

export interface RowState {
    /** The row to map; for a finished snapshot its score is the snapshot's. */
    match: MatchRow;
    isFinished: boolean;
    live?: LiveOverride;
}

/**
 * Effective state of one row: the latest live snapshot wins over the stored
 * status. In-play (LIVE / INTERRUPTED) renders as live (a stored in-play row
 * without a snapshot shows the stored score and the feed's current minute, if
 * any); a FINISHED snapshot renders as final with the
 * snapshot's score; otherwise a row is finished when it kicked off and is stored FINISHED.
 */
export function resolveRowState(match: MatchRow, snapshot: LiveEventSnapshot | undefined, now: Date): RowState {
    const status = snapshot?.status ?? match.status;
    if (status?.type && IN_PLAY_STATUSES.has(status.type)) {
        if (snapshot) {
            const data = toLiveMatchData(snapshot);
            return {
                match,
                isFinished: false,
                live: {
                    minute: data.currentMinute,
                    scoreOne: data.scoreCompetitorOne,
                    scoreTwo: data.scoreCompetitorTwo,
                },
            };
        }
        return {
            match,
            isFinished: false,
            live: {
                minute: storedMinute(match),
                scoreOne: match.score?.competitorOne ?? null,
                scoreTwo: match.score?.competitorTwo ?? null,
            },
        };
    }
    if (snapshot && status?.type === "FINISHED") {
        const data = toLiveMatchData(snapshot);
        return {
            match: {
                ...match,
                score: {
                    ...match.score,
                    competitorOne: data.scoreCompetitorOne ?? match.score?.competitorOne ?? null,
                    competitorTwo: data.scoreCompetitorTwo ?? match.score?.competitorTwo ?? null,
                },
            },
            isFinished: true,
        };
    }
    return { match, isFinished: new Date(match.timing.kickoffTime) < now && match.status?.type === "FINISHED" };
}
